import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react'

import { Popover, TableCell, Typography, IconButton } from '@mui/material'

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import { HighlightOff } from '@mui/icons-material'

import { useAuthContext } from '../../../../../hooks/useAuthContext'

import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  FIELD_TYPES,
  FORMATS,
  STANDARD_COLORS,
} from '../../../../../utility/constants'

import { listDataService } from '../../../../../services/listDataService'

import AutoCompleteField from '../../AutoCompleteField'
import MultiSelectField from '../../MultiSelectField'
import FileUploadField from '../../FileUploadField'
import FilePreviewField from '../../FilePreviewField'
import CurrencyField from '../../CurrencyField'
import BigTextField from '../../BigTextField'
import TextOrNumberField from '../../TextOrNumberField'
import DateAndDateTime from '../../DateAndDateTime'

import { fileServices } from '../../../../../services/fileServices'
import moment from 'moment'

import { DROPDOWN_DEFAULT_VALUE } from '../../../utils/autoCompute'
import WrapWithReviewSwitch from '../../../Components/WrapWithReviewSwitch'

import { getCommentId, getFieldId, getKeyOfValue, getReviewId } from '../../../utils/utils'

import { isReviewSwitchDisabled } from '../../../utils/fieldUtil'
import { useFieldCurrencyMap, useProcessContext } from '../../../../../store'

import { getColumnWidth } from '../../../../../../src/utility/columnWidth.js'

import { ON_CHANGE_DATA_TYPE } from '../TableField'
import { replacePlaceholders } from '../../../utils/placeholderUtil.js'
import { extractDisplayName } from '../../../../../utility/fileUtil.js'

import LongMenu from '../../LongMenu.js'
import FileViewer from '../../../../../components/Dashboard/PreviewComponent/components/FileViewer'
import FieldHistory from '../../../../../components/DataEntry/FieldHistory.js'

import { formatCurrency } from '../../../utils/currency.js'
import { formatUserFullName } from '../../../../../utility/userName'

import TimeStampedBigTextField from '../../TimeStampedBigTextField.js'
import TimeStampedDateField from '../../TimeStampedDateField.js'

import { indexedDbStorage } from '../../../../../utility/indexedDbStorage.js'
import { applyUserFilter } from '../../../../../utility/dataEntry/formData'
import { getRecurringPeriod } from '../../../../../utility/datetime.js'

const clampNumericString = (input, places) => {
  if (input === '' || input === null || input === undefined) return ''

  const str = String(input)

  const match = str.match(/^(-?\d+)(?:\.(\d*))?/)

  if (!match) return ''

  const [, intPart, fracPart = ''] = match

  if (places === null || places === undefined) {
    return match[0]
  }

  if (places === 0) {
    return intPart
  }

  const clampedFrac = fracPart.slice(0, places)

  return clampedFrac.length ? `${intPart}.${clampedFrac}` : intPart
}

const TableCellWrapper = ({
  field,
  rowId,
  isSelectable,
  isSelected,
  isImported,
  rowData,
  rowReview,
  rowComment,
  previousReviewDataOfCurrentRow,
  previousReviewCommentsDataOfCurrentRow,
  previousAllReviewsOfCurrentRow,
  onChangeValue,
  columnWidth = '100%',
  helperText = null,
  isFieldDisabled = () => {},
  syncWithParent = () => {},
  isReviewSwitchHidden = () => {},
  leftOffset,
  isFrozen,
  inputSx = {},
  allValues,
  isDataEntryModal = false,
  fieldDisabled,
  requireFullRowEntry,
  openDataEntryModal = () => {},
}) => {
  const { user } = useAuthContext()

  const [listData, setListData] = useState([])

  const [value, setValue] = useState(rowData?.[getFieldId(field.id)])

  const [listDataSynced, setListDataSynced] = useState([])

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)

  /*
   * --------------------------------------------------------------------------
   * Keep local value synchronized with row data.
   * --------------------------------------------------------------------------
   */
  useEffect(() => {
    setValue(rowData?.[getFieldId(field.id)])
  }, [rowData, field.id])

  /*
   * --------------------------------------------------------------------------
   * Table cell ref / width
   * --------------------------------------------------------------------------
   */
  const tableCellRef = useRef(null)

  const [calculatedMaxWidth, setCalculatedMaxWidth] = useState(null)

  /*
   * --------------------------------------------------------------------------
   * Popover state
   * --------------------------------------------------------------------------
   */
  const [popOverAnchorEl, setPopOVerAnchorEl] = useState(null)
  const [popOverOpen, setPopOverOpen] = useState(false)
  const [popOverId, setPopOverId] = useState(undefined)
  const [popOverContentWidth, setPopOverContentWidth] = useState(null)

  /*
   * --------------------------------------------------------------------------
   * Process context
   * --------------------------------------------------------------------------
   */
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)

  const processContext = useProcessContext((state) => state.processContext)

  const activityType = processContext?.activityTemplate?.activity_type

  const processStatus = processContext?.processData?.status

  const defaultCurrency = processContext?.companyDetails?.default_currency_code

  const version = processContext?.processData?.version

  const dynamicListData = processContext?.dynamicListData

  const teamsList = useProcessContext((state) => state.processContext.dynamicListData.teamsList)

  /*
   * --------------------------------------------------------------------------
   * Stable maps
   * --------------------------------------------------------------------------
   */
  const listDataIdMap = useMemo(
    () => processContext?.listDataIdMetadataMap ?? {},
    [processContext?.listDataIdMetadataMap],
  )

  const cellPlaceholderMap = useMemo(
    () =>
      processContext?.fieldMappings?.[field.id] ||
      processContext?.fieldMappings?.[String(field.id)] ||
      field?.metadata?.placeholder_map ||
      {},
    [processContext?.fieldMappings, field.id, field?.metadata?.placeholder_map],
  )

  const cellFieldMetadata = useMemo(
    () => processContext?.fieldMetadata ?? {},
    [processContext?.fieldMetadata],
  )

  const cellListHasPlaceholders =
    processContext?.listIdsWithPlaceholders instanceof Set
      ? processContext.listIdsWithPlaceholders.has(field.list_id)
      : null

  /*
   * --------------------------------------------------------------------------
   * Sync parent list data
   * --------------------------------------------------------------------------
   */
  useEffect(() => {
    setListDataSynced(syncWithParent(field, rowData, listData, teamsList))
  }, [field, rowData, listData, teamsList, syncWithParent])

  /*
   * --------------------------------------------------------------------------
   * Load list data
   * --------------------------------------------------------------------------
   */
  useEffect(() => {
    let isMounted = true

    ;(async () => {
      if (
        field.data_structure !== FIELD_TYPES.LIST &&
        field.data_structure !== FIELD_TYPES.LIST_MULTI
      ) {
        return
      }

      /*
       * Dynamic lists.
       */
      if (field.list_id == null) {
        const meta = field.dynamic_field_type
        const allData = processContext?.dynamicListData

        if (meta?.type === 'teams') {
          let nextListData = []

          if (meta?.supertype) {
            nextListData = allData?.teamsList
              ?.filter((it) => meta.supertype.includes(it.metadata.team_subtype))
              ?.filter((it) => meta?.subtypes.includes(it.metadata.team_type))
          } else {
            nextListData = allData?.teamsList?.filter(
              (it) =>
                meta?.subtypes?.includes(it.metadata.team_type) && it.metadata.team_subtype == null,
            )
          }

          if (isMounted) {
            setListData(nextListData || [])
          }
        } else if (meta?.type === 'users') {
          const filteredUsers = applyUserFilter(allData?.usersList || [], meta)

          if (isMounted) {
            setListData(filteredUsers)
          }
        } else {
          console.error('Dynamic Field Implementation Pending for ' + meta?.type)

          if (isMounted) {
            setListData([])
          }
        }

        return
      }

      /*
       * Static list.
       */
      const listDataId = `list_data_${field.list_id}`

      const listDataJson = await indexedDbStorage.getItem(listDataId)

      if (listDataJson) {
        if (isMounted) {
          setListData(listDataJson)
        }

        return
      }

      const listDataResponse = await listDataService.getData(user, field.list_id)

      if (isMounted) {
        setListData(listDataResponse.data)
      }

      await indexedDbStorage.setItem(listDataId, listDataResponse.data)
    })()

    return () => {
      isMounted = false
    }
  }, [
    field.id,
    field.list_id,
    field.data_structure,
    JSON.stringify(field.dynamic_field_type || null),
    processContext?.dynamicListData,
    user,
  ])

  /*
   * --------------------------------------------------------------------------
   * Selected list value
   * --------------------------------------------------------------------------
   */
  const selectedValues = useMemo(() => {
    return listDataSynced.find((it) => it.id == value)
  }, [value, listDataSynced])

  /*
   * --------------------------------------------------------------------------
   * Final disabled state
   * --------------------------------------------------------------------------
   */
  const isFieldFinallyDisabled = useMemo(() => {
    if (fieldDisabled !== undefined && fieldDisabled !== null) {
      return fieldDisabled
    }

    if (field?.is_read_only || activityType === ACTIVITY_TYPES.REVIEW) {
      return true
    }

    return isFieldDisabled(
      field,
      rowId,
      isSelected,
      isImported,
      previousReviewDataOfCurrentRow || {},
      rowReview,
      rowData,
    )
  }, [
    fieldDisabled,
    field,
    activityType,
    isFieldDisabled,
    rowId,
    isSelected,
    isImported,
    previousReviewDataOfCurrentRow,
    rowReview,
    rowData,
  ])

  /*
   * --------------------------------------------------------------------------
   * Central value updater.
   *
   * This is especially important for FILE fields.
   *
   * Any field change inside modal:
   *
   *     local state
   *          +
   *     parent row state
   *
   * are updated together.
   * --------------------------------------------------------------------------
   */
  const updateFieldValue = useCallback(
    (nextValue) => {
      setValue(nextValue)

      if (isDataEntryModal) {
        onChangeValue(field.id, nextValue, ON_CHANGE_DATA_TYPE.VALUE_DATA)
      }
    },
    [field.id, isDataEntryModal, onChangeValue],
  )

  /*
   * --------------------------------------------------------------------------
   * Field change handler.
   * --------------------------------------------------------------------------
   */
  const onChange = async (e, v) => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
        updateFieldValue(v?.id || DROPDOWN_DEFAULT_VALUE)
        break

      case FIELD_TYPES.LIST_MULTI:
        updateFieldValue(getTargetValues(v))
        break

      case FIELD_TYPES.FILE: {
        const file = e?.target?.files?.[0]

        if (!file) {
          return
        }

        const result = (await fileServices.uploadFile(user, file)).data

        const filePath = result?.filePath || ''

        const fileName = filePath.substring(filePath.lastIndexOf('/') + 1)

        updateFieldValue(fileName || '')

        break
      }

      case FIELD_TYPES.CURRENCY:
        if (e.floatValue !== undefined) {
          updateFieldValue(e.floatValue)
        } else {
          updateFieldValue(0)
        }
        break

      case FIELD_TYPES.BIGTEXT:
        updateFieldValue(e.target.value || '')
        break

      case FIELD_TYPES.TEXT:
        updateFieldValue(e.target.value || '')
        break

      case FIELD_TYPES.NUMBER: {
        const raw = e.target.value

        if (raw === '' || raw === null || raw === undefined) {
          updateFieldValue('')
          break
        }

        const preserveLeadingZeros = true

        if (preserveLeadingZeros) {
          updateFieldValue(raw)
          break
        }

        const places =
          typeof field?.num_decimal_places === 'number'
            ? field.num_decimal_places
            : field?.num_decimal_places === 0
              ? 0
              : null

        const clampedValue = clampNumericString(raw, places)

        let num = Number.parseFloat(clampedValue)

        if (!Number.isFinite(num)) {
          num = 0
        }

        updateFieldValue(num)

        break
      }

      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        updateFieldValue(e.target.value || '')
        break

      case FIELD_TYPES.TIMESTAMPED_DATE:
      case FIELD_TYPES.TIMESTAMPED_DATETIME:
        updateFieldValue(e.target.value || [])
        break

      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        updateFieldValue(e?.format('YYYY-MM-DD HH:mm:ss'))
        break

      default:
        console.log(`onChange - ${field.data_structure} is not supported!`)
    }
  }

  /*
   * --------------------------------------------------------------------------
   * Multi select helper.
   * --------------------------------------------------------------------------
   */
  const getTargetValues = (selectedItems) => {
    if (Array.isArray(selectedItems) && selectedItems.length) {
      return selectedItems.map((ele) => ele.id)
    }

    return DROPDOWN_DEFAULT_VALUE
  }

  /*
   * --------------------------------------------------------------------------
   * Display value.
   * --------------------------------------------------------------------------
   */
  const getActualValue = (currentValue) => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST: {
        if (cellListHasPlaceholders === false) {
          const entry = listDataIdMap[currentValue]

          return (
            entry?.list_data_value ||
            entry?.value ||
            listDataSynced.find((it) => it.id == currentValue)?.value ||
            ''
          )
        }

        const entry = listDataIdMap[currentValue]

        const listDataValue =
          entry?.list_data_value ||
          entry?.value ||
          listDataSynced.find((it) => it.id == currentValue)?.value

        if (!listDataValue?.includes('[[')) {
          return listDataValue ?? ''
        }

        return replacePlaceholders(
          listDataValue,
          {
            ...(processContext?.externalValues ?? {}),
            ...allValues,
            ...rowData,
          },
          listDataIdMap,
          cellPlaceholderMap,
          `__${rowId}`,
          cellFieldMetadata,
        )
      }

      case FIELD_TYPES.LIST_MULTI: {
        const vals = Array.isArray(currentValue) ? currentValue : []

        const listMultiActualValue = vals.map((val) => {
          const entry = listDataIdMap[val]

          const listDataValue =
            entry?.list_data_value ||
            entry?.value ||
            listDataSynced.find((it) => it.id == val)?.value ||
            ''

          if (cellListHasPlaceholders === false || !listDataValue?.includes('[[')) {
            return listDataValue
          }

          return replacePlaceholders(
            listDataValue,
            {
              ...(processContext?.externalValues ?? {}),
              ...allValues,
              ...rowData,
            },
            listDataIdMap,
            cellPlaceholderMap,
            `__${rowId}`,
            cellFieldMetadata,
          )
        })

        return listMultiActualValue.filter(Boolean).join(', ')
      }

      case FIELD_TYPES.CURRENCY:
        return formatCurrency(
          currentValue,
          getKeyOfValue(CURRENCY_COUNTRY_CODE, fieldCurrencyMap[field.id]),
          {
            defaultCurrency,
          },
        )

      case FIELD_TYPES.DATE: {
        if (field.is_recurring_subfield) {
          return getRecurringPeriod(currentValue, field.frequency).rangeText
        }
        const returnDate = moment(new Date(currentValue)).format(FORMATS.DATE)
        return returnDate === 'Invalid date' ? '' : returnDate
      }

      case FIELD_TYPES.DATETIME: {
        if (field.is_recurring_subfield) {
          return getRecurringPeriod(currentValue, field.frequency).rangeText
        }
        const returnDateTime = moment(new Date(currentValue)).format(FORMATS.DATETIME)
        return returnDateTime === 'Invalid date' ? '' : returnDateTime
      }

      case FIELD_TYPES.NUMBER: {
        const preserveLeadingZeros = true

        if (preserveLeadingZeros) {
          return currentValue ?? ''
        }

        const places =
          typeof field?.num_decimal_places === 'number'
            ? field.num_decimal_places
            : field?.num_decimal_places === 0
              ? 0
              : null

        return clampNumericString(currentValue, places)
      }

      case FIELD_TYPES.TIMESTAMPED_BIGTEXT: {
        if (!Array.isArray(currentValue)) {
          return ''
        }

        const latestEntry = currentValue?.[0] ? currentValue[0] : null

        const entryUser = latestEntry?.user_id
          ? dynamicListData?.users?.find((u) => u.id === latestEntry.user_id)
          : null

        const username = entryUser ? formatUserFullName(entryUser) : null

        return latestEntry ? `${latestEntry.timestamp} : ${latestEntry.text} - ${username}` : ''
      }

      case FIELD_TYPES.TIMESTAMPED_DATE:
      case FIELD_TYPES.TIMESTAMPED_DATETIME: {
        if (!Array.isArray(currentValue) || !currentValue[0]) {
          return ''
        }

        const latestDateEntry = currentValue[0]

        const dateUser = latestDateEntry?.user_id
          ? dynamicListData?.users?.find((u) => u.id === latestDateEntry.user_id)
          : null

        const dateUsername = dateUser ? formatUserFullName(dateUser) : null

        return `${latestDateEntry.timestamp} : ${latestDateEntry.text} - ${dateUsername}`
      }

      case FIELD_TYPES.FILE:
        return extractDisplayName(currentValue)

      default:
        return currentValue
    }
  }

  /*
   * --------------------------------------------------------------------------
   * Render actual input field.
   *
   * IMPORTANT:
   *
   * FILE rendering is based on `value`, not rowData.
   *
   *     no value -> upload
   *     value    -> preview
   *
   * This makes modal and popover behavior consistent.
   * --------------------------------------------------------------------------
   */
  const renderFields = () => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
        return (
          <AutoCompleteField
            field={field}
            fieldId={field.id}
            value={selectedValues || null}
            onChange={onChange}
            options={listDataSynced}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            autoFocus
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.LIST_MULTI:
        return (
          <MultiSelectField
            field={field}
            helperText={helperText}
            id={field.id}
            onChange={onChange}
            options={listDataSynced}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            autoFocus
          />
        )

      /*
       * ----------------------------------------------------------------------
       * FILE
       * ----------------------------------------------------------------------
       */
      case FIELD_TYPES.FILE:
        return !value ? (
          <FileUploadField
            field={field}
            id={field.id}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            tableField={!isDataEntryModal}
            inputSx={inputSx}
          />
        ) : (
          <FilePreviewField
            field={field}
            id={field.id}
            value={value}
            placeholder={field?.field_template_name}
            download={() => fileServices.downloadFile(user, value)}
            clear={(e) => {
              e?.stopPropagation?.()
              updateFieldValue('')
            }}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            isTableCell={!isDataEntryModal}
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.CURRENCY:
        return (
          <CurrencyField
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            autoFocus
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.BIGTEXT:
        return (
          <BigTextField
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            autoFocus
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        return (
          <TimeStampedBigTextField
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            autoFocus
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.TIMESTAMPED_DATE:
      case FIELD_TYPES.TIMESTAMPED_DATETIME:
        return (
          <TimeStampedDateField
            field={field}
            id={field.id}
            type={field.data_structure}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            autoFocus
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.TEXT:
      case FIELD_TYPES.NUMBER:
        return (
          <TextOrNumberField
            field={field}
            id={field.id}
            type={field.data_structure}
            onChange={onChange}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            autoFocus
            inputSx={inputSx}
          />
        )

      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        return (
          <DateAndDateTime
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            type={field?.data_structure}
            disabled={isFieldFinallyDisabled}
            helperText={helperText}
            tableField
            autoFocus
            inputSx={inputSx}
          />
        )

      default:
        return (
          <p
            style={{
              color: 'red',
            }}
          >
            {field.data_structure} - Not a valid field to render!!
          </p>
        )
    }
  }

  /*
   * --------------------------------------------------------------------------
   * Review helpers
   * --------------------------------------------------------------------------
   */
  const isReviewDisabled = useCallback(() => {
    return isReviewSwitchDisabled(
      activityType,
      processStatus,
      processContext?.processData?.modified_by,
      user.id,
    )
  }, [activityType, processStatus, processContext?.processData?.modified_by, user.id])

  const isSubsequentDataEntry = (currentActivityType, currentVersion) => {
    return currentActivityType === ACTIVITY_TYPES.DATA_ENTRY && currentVersion > 1
  }

  const getEffectiveReviewForCell = () => {
    const live = rowReview ? rowReview[getReviewId(field.id)] : undefined

    if (live !== undefined) {
      return live
    }

    if (activityType === ACTIVITY_TYPES.DATA_ENTRY && version > 1) {
      return previousReviewDataOfCurrentRow
        ? previousReviewDataOfCurrentRow[getReviewId(field.id)]
        : undefined
    }

    return live
  }

  /*
   * --------------------------------------------------------------------------
   * IMPORTANT:
   *
   * This is now the ONLY click entry point for a table cell.
   *
   * Modal mode:
   *     do nothing here because modal is already open.
   *
   * Normal table mode:
   *     openDataEntryModal()
   *
   * If there is no modal handler, fall back to popover.
   * --------------------------------------------------------------------------
   */
  const handleCellClick = (event, id, width) => {
    // Already inside the RowWrapper's data-entry modal.
    // Never open a popover.
    if (isDataEntryModal) {
      return
    }

    // RowWrapper owns the modal.
    // Delegate to it when full-row entry is enabled.
    if (requireFullRowEntry && isReviewDisabled(field)) {
      openDataEntryModal()
      return
    }

    // Otherwise this is normal table-cell mode.
    // Open the existing popover.
    if (activityType !== ACTIVITY_TYPES.REVIEW || field.data_structure === FIELD_TYPES.FILE) {
      setPopOVerAnchorEl(event.currentTarget)
      setPopOverId(id)
      setPopOverOpen(true)
      setPopOverContentWidth(width)

      const current = rowData[getFieldId(field.id)]

      if (field.data_structure === FIELD_TYPES.NUMBER) {
        const preserveLeadingZeros = true

        if (preserveLeadingZeros) {
          setValue(current === undefined || current === null ? '' : String(current))
        } else {
          const places =
            typeof field?.num_decimal_places === 'number'
              ? field.num_decimal_places
              : field?.num_decimal_places === 0
                ? 0
                : null

          const clamped = clampNumericString(current, places)

          if (clamped === '') {
            setValue('')
          } else {
            const num = Number.parseFloat(clamped)

            setValue(Number.isFinite(num) ? num : 0)
          }
        }
      } else {
        setValue(current)
      }
    }
  }

  /*
   * --------------------------------------------------------------------------
   * Popover close.
   * --------------------------------------------------------------------------
   */
  const popOverHandleClose = () => {
    if (isSelectable && isSelected && !field.is_read_only) {
      if (value !== rowData[getFieldId(field.id)]) {
        onChangeValue(field.id, value, ON_CHANGE_DATA_TYPE.VALUE_DATA)
      }
    } else if (!isSelectable && !field.is_read_only) {
      if (value !== rowData[getFieldId(field.id)]) {
        onChangeValue(field.id, value, ON_CHANGE_DATA_TYPE.VALUE_DATA)
      }
    }

    setPopOVerAnchorEl(null)
    setPopOverId(undefined)
    setPopOverOpen(false)
    setPopOverContentWidth(null)
  }

  /*
   * --------------------------------------------------------------------------
   * Enter key handling.
   * --------------------------------------------------------------------------
   */
  const popOverOnEnterKeyPress = (e, dataStructure) => {
    if (
      e.key === 'Enter' &&
      [
        FIELD_TYPES.BIGTEXT,
        FIELD_TYPES.TIMESTAMPED_BIGTEXT,
        FIELD_TYPES.TIMESTAMPED_DATE,
        FIELD_TYPES.TIMESTAMPED_DATETIME,
      ].includes(dataStructure)
    ) {
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      popOverHandleClose()
    }
  }

  /*
   * --------------------------------------------------------------------------
   * File preview.
   * --------------------------------------------------------------------------
   */
  const handlePreviewClick = (fileKey) => {
    if (!fileKey) {
      return
    }

    setIsFileViewerOpen(true)
  }

  /*
   * --------------------------------------------------------------------------
   * Calculate available width.
   * --------------------------------------------------------------------------
   */
  useEffect(() => {
    const calculateWidth = () => {
      if (!tableCellRef.current) {
        return
      }

      const tableCellWidth = tableCellRef.current.offsetWidth

      const reviewSwitchWidth = isReviewSwitchHidden(field) ? 0 : 80

      const fileActionsWidth =
        field.data_structure === FIELD_TYPES.FILE && rowData[getFieldId(field.id)] ? 75 : 0

      const fieldHistoryWidth = 10

      const availableWidth =
        tableCellWidth - reviewSwitchWidth - fileActionsWidth - fieldHistoryWidth

      setCalculatedMaxWidth(Math.max(availableWidth, 100))
    }

    calculateWidth()

    const handleResize = () => {
      setTimeout(calculateWidth, 100)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [field, rowData, isReviewSwitchHidden])

  /*
   * --------------------------------------------------------------------------
   * Resize observer.
   * --------------------------------------------------------------------------
   */
  useEffect(() => {
    if (!tableCellRef.current) {
      return
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect

        const reviewSwitchWidth = isReviewSwitchHidden(field)
          ? 0
          : field.data_structure === FIELD_TYPES.FILE
            ? 80
            : 48

        const hasFileActions =
          field.data_structure === FIELD_TYPES.FILE && rowData[getFieldId(field.id)]

        const fileActionsWidth = hasFileActions ? 15 : 0

        let spacingAdjustment = 0

        if (reviewSwitchWidth > 0 && fileActionsWidth > 0) {
          spacingAdjustment = 18
        } else if (field.data_structure === FIELD_TYPES.FILE && reviewSwitchWidth > 0) {
          spacingAdjustment = 18
        }

        const totalReservedWidth = reviewSwitchWidth + fileActionsWidth - spacingAdjustment

        const paddingBuffer = 0

        const availableWidth = width - totalReservedWidth - paddingBuffer

        setCalculatedMaxWidth(Math.max(availableWidth, 100))
      }
    })

    resizeObserver.observe(tableCellRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [field, rowData, isReviewSwitchHidden])

  /*
   * --------------------------------------------------------------------------
   * DATA ENTRY MODAL
   *
   * No TableCell.
   * No Popover.
   *
   * Just render the actual field.
   * --------------------------------------------------------------------------
   */
  if (isDataEntryModal) {
    return (
      <>
        {renderFields()}

        {field.data_structure === FIELD_TYPES.FILE && (
          <FileViewer
            open={isFileViewerOpen}
            onClose={() => setIsFileViewerOpen(false)}
            fileKey={value}
            user={user}
            onDownload={() => fileServices.downloadFile(user, value)}
          />
        )}
      </>
    )
  }

  /*
   * --------------------------------------------------------------------------
   * NORMAL TABLE CELL
   * --------------------------------------------------------------------------
   */
  return (
    <>
      <TableCell
        ref={tableCellRef}
        aria-describedby={`popover_${field.id}__${rowId}`}
        sx={{
          backgroundColor: isFrozen ? 'lightgrey' : 'white',
          padding: '4px 4px',
          width: columnWidth,
          maxWidth: getColumnWidth(field, isReviewSwitchHidden),
          minWidth: getColumnWidth(field, isReviewSwitchHidden),
          position: isFrozen ? 'sticky' : 'static',
          left: isFrozen ? `${leftOffset}px` : 'auto',
          top: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <WrapWithReviewSwitch
          field={field}
          review={getEffectiveReviewForCell()}
          comment={
            isSubsequentDataEntry(activityType, version)
              ? previousReviewCommentsDataOfCurrentRow
                ? previousReviewCommentsDataOfCurrentRow[getCommentId(field.id)]
                : ''
              : rowComment
                ? rowComment[getCommentId(field.id)]
                : ''
          }
          previousReviews={
            previousAllReviewsOfCurrentRow
              ? previousAllReviewsOfCurrentRow[getReviewId(field.id)]
              : []
          }
          onCommentChange={(cmt) => onChangeValue(field.id, cmt, ON_CHANGE_DATA_TYPE.COMMENT_DATA)}
          onReviewChange={(rvw) => onChangeValue(field.id, rvw, ON_CHANGE_DATA_TYPE.REVIEW_DATA)}
          disabled={isReviewDisabled(field)}
          row={rowId}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <FieldHistory
              fieldId={`${field.id}__${rowId}`}
              rowId={rowId}
              field={field}
              dynamicListData={dynamicListData}
              processId={processContext?.processData?.id}
              allValues={allValues}
              helperText={helperText}
            >
              <div
                style={{
                  padding: '5px',
                  border: '1px solid',
                  borderColor:
                    helperText?.length || (helperText?.length > 0 && isSelectable && isSelected)
                      ? '#d32f2f'
                      : 'rgb(195 195 195)',
                  borderRadius: '4px',
                  backgroundColor: (() => {
                    const rvw = getEffectiveReviewForCell()

                    if (rvw === true) {
                      return STANDARD_COLORS.BG_READONLY
                    }

                    if (rvw === false) {
                      return STANDARD_COLORS.BG_EDITABLE
                    }

                    if (isFieldFinallyDisabled) {
                      return STANDARD_COLORS.BG_READONLY
                    }

                    return STANDARD_COLORS.BG_EDITABLE
                  })(),
                  height: 'auto',
                  minHeight: '30px',
                  maxHeight: '30px',
                  maxWidth: calculatedMaxWidth ? `${calculatedMaxWidth}px` : '100%',
                  width: '100%',
                }}
                onClick={(e) =>
                  handleCellClick(
                    e,
                    `popover_${field.id}__${rowId}`,
                    e.currentTarget.getBoundingClientRect().width + 100,
                  )
                }
              >
                <Typography
                  fontSize="small"
                  noWrap
                  align={
                    [FIELD_TYPES.CURRENCY, FIELD_TYPES.NUMBER].includes(field.data_structure)
                      ? 'right'
                      : 'left'
                  }
                  style={{
                    textOverflow: 'ellipsis',
                    color: (() => {
                      const rvw = getEffectiveReviewForCell()

                      if (rvw === true) {
                        return STANDARD_COLORS.TEXT_ACCEPTED
                      }

                      if (rvw === false) {
                        return STANDARD_COLORS.TEXT_REJECTED
                      }

                      return STANDARD_COLORS.TEXT_DEFAULT
                    })(),
                  }}
                >
                  <div className="field-wrapper">
                    {getActualValue(rowData[getFieldId(field.id)])}
                  </div>
                </Typography>
              </div>
            </FieldHistory>
            {field.data_structure === FIELD_TYPES.FILE && rowData[getFieldId(field.id)] && (
              <LongMenu
                options={[
                  {
                    label: 'Preview',
                    icon: (
                      <IconButton size="small">
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    ),
                    onClick: () => handlePreviewClick(value),
                  },
                  {
                    label: 'Download',
                    icon: (
                      <IconButton size="small">
                        <GetAppOutlinedIcon color="info" size="small" />
                      </IconButton>
                    ),
                    onClick: () => fileServices.downloadFile(user, value),
                  },
                  !isFieldFinallyDisabled && {
                    label: 'Delete',
                    icon: (
                      <IconButton size="small" disabled={isFieldFinallyDisabled}>
                        <DeleteOutlinedIcon disabled={isFieldFinallyDisabled} size="small" />
                      </IconButton>
                    ),
                    onClick: isFieldFinallyDisabled
                      ? undefined
                      : () => onChangeValue(field.id, '', ON_CHANGE_DATA_TYPE.VALUE_DATA),
                  },
                ].filter(Boolean)}
              />
            )}
          </div>
        </WrapWithReviewSwitch>
      </TableCell>
      {field.data_structure === FIELD_TYPES.FILE && (
        <FileViewer
          open={isFileViewerOpen}
          onClose={() => setIsFileViewerOpen(false)}
          fileKey={value}
          user={user}
          onDownload={() => fileServices.downloadFile(user, value)}
        />
      )}

      {!isDataEntryModal && !isFieldFinallyDisabled && (
        <Popover
          id={popOverId}
          open={popOverOpen}
          anchorEl={popOverAnchorEl}
          onClose={popOverHandleClose}
          onKeyDownCapture={(e) => popOverOnEnterKeyPress(e, field.data_structure)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
        >
          <div
            style={{
              padding: '0px 12px 12px 12px',
              width: popOverContentWidth,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <IconButton size="small" onClick={popOverHandleClose}>
                <HighlightOff fontSize="10px" />
              </IconButton>
            </div>

            <div>{popOverOpen && renderFields()}</div>
          </div>
        </Popover>
      )}
    </>
  )
}

export default memo(
  TableCellWrapper,
  (prevProps, nextProps) =>
    prevProps.rowData[getFieldId(prevProps.field.id)] ===
      nextProps.rowData[getFieldId(nextProps.field.id)] &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.rowData.updatedAt === nextProps.rowData.updatedAt &&
    prevProps.rowReview?.updatedAt === nextProps.rowReview?.updatedAt &&
    prevProps.rowComment?.updatedAt === nextProps.rowComment?.updatedAt &&
    prevProps.isFrozen === nextProps.isFrozen &&
    prevProps.leftOffset === nextProps.leftOffset &&
    prevProps.helperText === nextProps.helperText &&
    prevProps.fieldDisabled === nextProps.fieldDisabled &&
    prevProps.isDataEntryModal === nextProps.isDataEntryModal &&
    prevProps.openDataEntryModal === nextProps.openDataEntryModal,
)
